import { Controller } from "@hotwired/stimulus"

// Present a native select as visible choices while keeping its form value and
// change events intact for the estimate editor and autosave controllers.
export default class extends Controller {
  static targets = ["select", "choices"]

  connect() {
    this.selectTarget.classList.add("hidden")
    this.render()
    this.observer = new MutationObserver(() => this.render())
    this.observer.observe(this.selectTarget, { childList: true, subtree: true })
  }

  disconnect() {
    this.observer?.disconnect()
    this.selectTarget.classList.remove("hidden")
  }

  selectionChanged() {
    this.choicesTarget.querySelectorAll("button[data-value]").forEach(button => {
      const selected = button.dataset.value === this.selectTarget.value
      // The pill style follows aria-pressed (see the class list below)
      button.setAttribute("aria-pressed", String(selected))
    })
  }

  choose(event) {
    const value = event.currentTarget.dataset.value
    if (value === this.selectTarget.value) return

    this.selectTarget.value = value
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  render() {
    this.choicesTarget.replaceChildren()

    // The number of colours of a screen printed logo is required (marking wizard): no blank choice, bare numbers
    const colourCount = this.element.dataset.colourCount === "true"
    const options = [...this.selectTarget.options].filter(option => !option.disabled && !(colourCount && option.value === "" && this.selectTarget.options.length > 1))
    if (options.length === 1 && options[0].value === "") {
      const empty = document.createElement("span")
      empty.className = "text-sm text-erp-muted"
      empty.textContent = this.element.dataset.emptyLabel || options[0].textContent
      this.choicesTarget.appendChild(empty)
      return
    }

    options.forEach(option => {
      const button = document.createElement("button")
      button.type = "button"
      button.dataset.value = option.value
      button.dataset.action = "erp--select-chips#choose"
      button.className = "inline-flex min-h-10 cursor-pointer items-center rounded-full border border-erp-line-strong bg-erp-surface px-4 text-[13px] font-bold text-erp-ink transition-colors hover:border-erp-black aria-pressed:border-erp-black aria-pressed:bg-erp-black aria-pressed:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-erp-black"
      button.textContent = this.#labelFor(option)
      this.choicesTarget.appendChild(button)
    })

    this.selectionChanged()
  }

  #labelFor(option) {
    const technique = this.element.closest("[data-erp--marking-form-structured-pricing-value]")
      ?.querySelector("[data-erp--marking-form-target='technique']")?.value
    if (option.value === "") {
      if (technique === "dtg") return this.element.dataset.standardLabel || "Standard"
      return this.element.dataset.blankLabel || option.textContent
    }
    if (option.value === "0" && this.element.dataset.zeroLabel) return this.element.dataset.zeroLabel
    if (technique === "dtg" && option.value === "prototype") {
      return this.element.dataset.prototypeLabel || "Prototype"
    }

    if (this.element.dataset.colourCount === "true" && /^\d+$/.test(option.value)) return option.value
    if (technique === "screen_print" && /^\d+$/.test(option.value) && this.element.dataset.colorSingular) {
      const unit = option.value === "1" ? this.element.dataset.colorSingular : this.element.dataset.colorPlural
      return `${option.value} ${unit}`
    }
    if (technique === "embroidery" && this.element.dataset.stitchUnit) {
      const dimension = option.textContent.trim()
        .replace(/\d{4,}/g, value => new Intl.NumberFormat("fr-FR").format(Number(value)))
        .replace(/(\d)-(\d)/g, "$1–$2")
      return `${dimension} ${this.element.dataset.stitchUnit}`
    }

    return option.textContent
  }
}
